import { nextTick, ref } from "vue";

import { dragActions } from "@/action/dragActions";
import { internalActions } from "@/action/internalActions";
import { applyCoord, clampCoord, divided, minus, plus, times } from "@/math/coordinates";
import type { Card } from "@/model/card";
import type { RelativeCoordinate } from "@/model/coordinates";
import { relativeCoord } from "@/model/coordinates";
import { halfCardSize, useBoardStore } from "@/store/board";
import { useDraggingStore } from "@/store/dragging";
import { useGlobalI18n } from "@/translations/i18n";
import { terminateEvent } from "@/utils/dom/dom";

import type { CardMetaInjected } from "../injectKeys";

export type Props = Readonly<{ card: Card; cardMeta: CardMetaInjected; deactivate: () => void }>;

export function useKeyboardDrag({ card, cardMeta, deactivate }: Props) {
  const { t } = useGlobalI18n();

  const moveMode = ref(false);
  const screenReaderMessage = ref("");
  const boardStore = useBoardStore();

  /**
   * Treat the user moving the sticky by keyboard as a drag operation
   * (so eg. the action menu is hidden)
   */
  const startMove = () => {
    dragActions.startCardDrag("keyboard", [{ id: card.id, pos: cardMeta.pos }]);
    screenReaderMessage.value = t("label.moveSticky.controlsActivated");
    moveMode.value = true;
  };

  /**
   * Returns the current position of the sticky note, taking into account the current drag position
   */
  const getCurrentPos = (): RelativeCoordinate => {
    const dragCoords = { ...useDraggingStore().findById(card.id)?.pos };
    return relativeCoord(dragCoords.x || cardMeta.pos.x, dragCoords.y || cardMeta.pos.y);
  };

  /**
   * Key handler for the sticky note accessibility controls
   * If user is in moveMode, passes the event to handleMoveKey
   * Otherwise:
   * - Enter/Space/M: Start moving the sticky note
   * - Escape: Deactivate (focus on sticky)
   */
  const handleKeydown = (event: KeyboardEvent) => {
    if (moveMode.value) {
      event.preventDefault();
      event.stopImmediatePropagation();

      handleMoveKey(event);
    } else if (["Enter", " ", "m"].includes(event.key)) {
      terminateEvent(event);
      startMove();
    } else if (event.key === "Escape") {
      terminateEvent(event);
      deactivate();
    }
  };

  /**
   * Handle the keyboard interaction while user is moving the sticky note
   * - Escape: Cancel the move (reset to original position)
   * - Enter/Space: Save the new position and stop dragging
   * - M: Stop dragging, save the new position, and focus on the sticky
   * - Arrow keys: Move the sticky note in the given direction
   */
  const handleMoveKey = (event: KeyboardEvent) => {
    switch (event.key) {
      case "Escape":
        useDraggingStore().dragCard(card.id, cardMeta.pos);
        stopMove();
        screenReaderMessage.value = t("label.moveSticky.moveCancelled");
        break;
      case "Enter":
      case " ":
        stopMove();
        break;
      case "m":
        // Save the new position, stop dragging, and focus on the sticky
        stopMove();
        deactivate();
        break;
      case "ArrowUp":
        moveSticky(0, -1);
        break;
      case "ArrowDown":
        moveSticky(0, 1);
        break;
      case "ArrowLeft":
        moveSticky(-1, 0);
        break;
      case "ArrowRight":
        moveSticky(1, 0);
        break;
      default:
        break;
    }
  };

  /**
   * Move the sticky note in the given direction
   * (in increments of 1/3 of the current card size)
   * and announce the change to screen reader users
   */
  const moveSticky = (x: number, y: number) => {
    const distance = divided(boardStore.currentBoard().cardSize, 3);
    const currentPos = getCurrentPos();
    const movedPos = plus(currentPos, times(relativeCoord(x, y), distance));
    const [newPos, delta] = cardInsideBoardRelative(movedPos);
    dragActions.dragCard("keyboard", card.id, newPos);

    /*
      Finally, communicate the changes to screen-reader users
    */

    const originalSection = boardStore.boardLocation(currentPos);
    const newSection = boardStore.boardLocation(newPos);

    const reachedEdge = delta.x || delta.y;

    // If the sticky note is moved into a new section, announce it
    if (originalSection.id() !== newSection.id()) {
      screenReaderMessage.value = t("label.moveSticky.intoSection", {
        from: originalSection.name,
        to: newSection.name,
      });
    } else if (reachedEdge) {
      // If the sticky note is at the edge of the board, let the user know
      screenReaderMessage.value = t("label.moveSticky.edgeOfBoard");
    } else {
      // otherwise announce how far across the board the sticky is
      const scaled = applyCoord(times(newPos, 100), Math.round);
      const msgPosition = { top: scaled.y, left: scaled.x, section: newSection.name };

      screenReaderMessage.value = t("label.moveSticky.moved", msgPosition);
    }

    // Scroll the sticky note into view (after the new position is rendered)
    void nextTick(() => document.getElementById(card.id)?.scrollIntoView({ block: "nearest" }));
  };

  /**
   * If moving, end the drag operation and save the new position
   */
  const stopMove = () => {
    if (moveMode.value) {
      screenReaderMessage.value = t("label.moveSticky.controlsDeactivated");
      const newPos = getCurrentPos();
      dragActions.endCardDrag("keyboard", [{ id: card.id, pos: newPos }]);
      internalActions.stopAlter(card.id);
    } else {
      screenReaderMessage.value = "";
    }

    moveMode.value = false;
  };

  return { screenReaderMessage, moveMode, handleKeydown, startMove, stopMove };
}

/**
 * Returns the given position of the center of the sticky, corrected to bring
 * it inside the board at the nearest edge if needed
 *
 * @param pos Position of the center of the card
 * @returns [corrected_position, delta_(offset_from_pos)]
 */
export function cardInsideBoardRelative(
  pos: RelativeCoordinate,
): [RelativeCoordinate, RelativeCoordinate] {
  const half = halfCardSize();
  const newPos = clampCoord(pos, half, minus(relativeCoord(1, 1), half));
  const delta = minus(newPos, pos);
  return [newPos, delta];
}
